import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Search, Upload, Globe, Mail, Phone, User, Shield, Loader2, ExternalLink, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/intelligence`;

const TABS = [
  { id: 'image', label: 'Image Intel', icon: Upload },
  { id: 'person', label: 'Person Search', icon: User },
  { id: 'domain', label: 'Domain Intel', icon: Globe },
  { id: 'email', label: 'Email Intel', icon: Mail },
  { id: 'phone', label: 'Phone Intel', icon: Phone },
];

const MHIntelligence = () => {
  const [activeTab, setActiveTab] = useState('image');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const resetResults = () => { setResults(null); setError(''); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1437] via-[#1a2556] to-[#0B1437]">
      <div className="px-4 sm:px-6 py-6 bg-[#0B1437]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-1">
            <Shield size={28} className="text-[#FF6B35]" />
            <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-semibold text-white" data-testid="intelligence-title">MH Intelligence</h1>
          </div>
          <p className="text-sm text-slate-400 ml-10">OSINT & Investigation Suite</p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-hide" data-testid="intel-tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); resetResults(); }}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white shadow-lg'
                    : 'bg-[#1a2556]/60 border border-white/10 text-slate-300 hover:border-[#FF6B35]/50'
                }`}
                style={activeTab === tab.id ? { boxShadow: '0 0 20px rgba(255, 107, 53, 0.3)' } : {}}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-xl text-red-200 text-sm flex items-center gap-2" data-testid="intel-error">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
            {activeTab === 'image' && <ImageIntel loading={loading} setLoading={setLoading} results={results} setResults={setResults} setError={setError} />}
            {activeTab === 'person' && <PersonSearch loading={loading} setLoading={setLoading} results={results} setResults={setResults} setError={setError} />}
            {activeTab === 'domain' && <DomainIntel loading={loading} setLoading={setLoading} results={results} setResults={setResults} setError={setError} />}
            {activeTab === 'email' && <EmailIntel loading={loading} setLoading={setLoading} results={results} setResults={setResults} setError={setError} />}
            {activeTab === 'phone' && <PhoneIntel loading={loading} setLoading={setLoading} results={results} setResults={setResults} setError={setError} />}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
};

// --- Image Intelligence Tab ---
const ImageIntel = ({ loading, setLoading, results, setResults, setError }) => {
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(f);
    setResults(null);
  };

  const analyze = async () => {
    if (!file) return;
    setLoading(true); setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/analyze-image`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      setResults(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Image analysis failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      {/* Upload + Search button */}
      <div className="card-premium rounded-2xl p-6">
        <h3 className="text-base font-semibold text-white mb-4">Reverse Face / Image Search</h3>
        <div className="flex flex-col sm:flex-row gap-4 items-start">
          <label className="flex flex-col items-center justify-center w-full sm:w-64 h-48 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-[#FF6B35]/50 transition-colors bg-[#0B1437]/40 flex-shrink-0" data-testid="image-upload-area">
            {preview ? (
              <img src={preview} alt="Preview" className="max-h-44 rounded-lg object-contain" />
            ) : (
              <div className="text-center">
                <Upload size={32} className="mx-auto text-slate-400 mb-2" />
                <p className="text-slate-400 text-sm">Click to upload photo</p>
                <p className="text-slate-500 text-xs mt-1">JPEG, PNG, WEBP</p>
              </div>
            )}
            <input type="file" className="hidden" accept="image/jpeg,image/png,image/webp" onChange={(e) => handleFile(e.target.files[0])} />
          </label>
          <div className="flex-1 w-full">
            <p className="text-slate-400 text-sm mb-3">Upload a face photo to find matching images across the internet. Results include visual matches with thumbnails, source links, and AI-powered analysis.</p>
            <button
              onClick={analyze}
              disabled={!file || loading}
              className="w-full py-3 bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white font-bold rounded-xl disabled:opacity-40 transition-all"
              data-testid="analyze-image-btn"
            >
              {loading ? <span className="flex items-center justify-center gap-2"><Loader2 size={18} className="animate-spin" /> Searching the web...</span> : 'Search Face / Image'}
            </button>
          </div>
        </div>
      </div>

      {/* Visual Matches - THE MAIN RESULT */}
      {results && results.visual_matches && results.visual_matches.length > 0 && (
        <div className="card-premium rounded-2xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-base font-semibold text-white">Visual Matches Found</h3>
            <span className="text-[#FF6B35] font-bold text-lg" data-testid="match-count">{results.match_count} matches</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="visual-matches">
            {results.visual_matches.map((match, i) => (
              <a
                key={i}
                href={match.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-xl overflow-hidden bg-[#0B1437]/60 border border-white/10 hover:border-[#FF6B35]/50 transition-all duration-200 hover:shadow-lg hover:shadow-[#FF6B35]/10"
                data-testid={`match-${i}`}
              >
                {match.thumbnail && (
                  <div className="w-full h-36 bg-[#0B1437] flex items-center justify-center overflow-hidden">
                    <img src={match.thumbnail} alt={match.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                  </div>
                )}
                <div className="p-3">
                  <p className="text-white text-sm font-medium line-clamp-2 mb-1">{match.title || 'Untitled'}</p>
                  <p className="text-[#FF6B35] text-xs truncate">{match.source || new URL(match.link).hostname}</p>
                  <div className="flex items-center gap-1 mt-2 text-slate-400 text-xs">
                    <ExternalLink size={10} /> <span>View source</span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {results && (!results.visual_matches || results.visual_matches.length === 0) && (
        <div className="card-premium rounded-2xl p-6 text-center">
          <p className="text-slate-400">No visual matches found across the web for this image.</p>
        </div>
      )}

      {/* AI Analysis - Secondary, collapsible */}
      {results && results.analysis && (
        <details className="card-premium rounded-2xl p-6" open>
          <summary className="text-base font-semibold text-white cursor-pointer hover:text-[#FF6B35] transition-colors">AI Intelligence Report</summary>
          <div className="mt-4 prose prose-invert prose-sm max-w-none text-slate-300 whitespace-pre-wrap" data-testid="image-report">{results.analysis}</div>
        </details>
      )}
    </div>
  );
};

// --- Person Search Tab ---
const PersonSearch = ({ loading, setLoading, results, setResults, setError }) => {
  const [username, setUsername] = useState('');

  const search = async () => {
    if (!username.trim()) return;
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(`${API}/person-search`, { username: username.trim() }, { withCredentials: true });
      setResults(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Search failed');
    } finally { setLoading(false); }
  };

  return (
    <div>
      <div className="card-premium rounded-2xl p-6 mb-6">
        <h3 className="text-base font-semibold text-white mb-4">Username / Person Search</h3>
        <p className="text-slate-400 text-xs mb-4">Search across 24+ social platforms for matching profiles</p>
        <div className="flex gap-3">
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && search()}
            placeholder="Enter username..."
            className="flex-1 bg-[#0B1437]/80 border border-white/10 rounded-xl h-11 px-4 text-white text-sm focus:outline-none focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35]"
            data-testid="person-search-input"
          />
          <button onClick={search} disabled={loading || !username.trim()} className="px-6 py-2.5 bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white font-bold rounded-xl disabled:opacity-40" data-testid="person-search-btn">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
          </button>
        </div>
      </div>

      {results && (
        <div className="space-y-4">
          <div className="card-premium rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-white">Results for "{results.username}"</h3>
              <span className="text-[#FF6B35] font-bold text-lg" data-testid="found-count">{results.found_count}/{results.total_checked}</span>
            </div>
            {results.found_count > 0 && (
              <div className="space-y-2">
                {results.found_profiles.map((p) => (
                  <a key={p.platform} href={p.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-3 rounded-xl bg-[#22c55e]/10 border border-[#22c55e]/20 hover:border-[#22c55e]/40 transition-all" data-testid={`found-${p.icon}`}>
                    <div className="flex items-center gap-3">
                      <CheckCircle size={16} className="text-[#22c55e]" />
                      <span className="text-white text-sm font-medium">{p.platform}</span>
                    </div>
                    <ExternalLink size={14} className="text-slate-400" />
                  </a>
                ))}
              </div>
            )}
          </div>
          {results.not_found.length > 0 && (
            <details className="card-premium rounded-2xl p-5">
              <summary className="text-slate-400 text-sm cursor-pointer hover:text-white transition-colors">Not found ({results.not_found.length} platforms)</summary>
              <div className="mt-3 space-y-1">
                {results.not_found.map((p) => (
                  <div key={p.platform} className="flex items-center gap-2 py-1.5 text-slate-500 text-xs">
                    <XCircle size={12} /> {p.platform}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

// --- Domain Intel Tab ---
const DomainIntel = ({ loading, setLoading, results, setResults, setError }) => {
  const [domain, setDomain] = useState('');

  const lookup = async () => {
    if (!domain.trim()) return;
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(`${API}/domain-lookup`, { domain: domain.trim() }, { withCredentials: true });
      setResults(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Lookup failed');
    } finally { setLoading(false); }
  };

  return (
    <div>
      <div className="card-premium rounded-2xl p-6 mb-6">
        <h3 className="text-base font-semibold text-white mb-4">Domain Intelligence</h3>
        <p className="text-slate-400 text-xs mb-4">WHOIS data, DNS records, nameservers, registrar info</p>
        <div className="flex gap-3">
          <input value={domain} onChange={(e) => setDomain(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && lookup()} placeholder="example.com" className="flex-1 bg-[#0B1437]/80 border border-white/10 rounded-xl h-11 px-4 text-white text-sm focus:outline-none focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35]" data-testid="domain-input" />
          <button onClick={lookup} disabled={loading || !domain.trim()} className="px-6 py-2.5 bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white font-bold rounded-xl disabled:opacity-40" data-testid="domain-search-btn">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
          </button>
        </div>
      </div>

      {results && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {results.whois && (
            <div className="card-premium rounded-2xl p-5">
              <h3 className="text-base font-semibold text-white mb-4">WHOIS Data</h3>
              <div className="space-y-2" data-testid="whois-data">
                {Object.entries(results.whois).map(([key, val]) => (
                  <div key={key} className="flex flex-col sm:flex-row sm:justify-between py-1.5 border-b border-white/5">
                    <span className="text-xs uppercase text-slate-400 tracking-wider">{key.replace(/_/g, ' ')}</span>
                    <span className="text-sm text-white font-mono break-all">{Array.isArray(val) ? val.join(', ') : val}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {Object.keys(results.dns).length > 0 && (
            <div className="card-premium rounded-2xl p-5">
              <h3 className="text-base font-semibold text-white mb-4">DNS Records</h3>
              <div className="space-y-3" data-testid="dns-data">
                {Object.entries(results.dns).map(([rtype, records]) => (
                  <div key={rtype}>
                    <span className="text-xs font-bold text-[#FF6B35] uppercase">{rtype}</span>
                    {records.map((r, i) => (
                      <p key={i} className="text-sm text-slate-300 font-mono ml-3 break-all">{r}</p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- Email Intel Tab ---
const EmailIntel = ({ loading, setLoading, results, setResults, setError }) => {
  const [email, setEmail] = useState('');

  const lookup = async () => {
    if (!email.trim()) return;
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(`${API}/email-lookup`, { email: email.trim() }, { withCredentials: true });
      setResults(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Lookup failed');
    } finally { setLoading(false); }
  };

  return (
    <div>
      <div className="card-premium rounded-2xl p-6 mb-6">
        <h3 className="text-base font-semibold text-white mb-4">Email Intelligence</h3>
        <p className="text-slate-400 text-xs mb-4">Email validation, MX records, provider detection, profile suggestions</p>
        <div className="flex gap-3">
          <input value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && lookup()} placeholder="user@example.com" className="flex-1 bg-[#0B1437]/80 border border-white/10 rounded-xl h-11 px-4 text-white text-sm focus:outline-none focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35]" data-testid="email-input" />
          <button onClick={lookup} disabled={loading || !email.trim()} className="px-6 py-2.5 bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white font-bold rounded-xl disabled:opacity-40" data-testid="email-search-btn">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
          </button>
        </div>
      </div>

      {results && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card-premium rounded-2xl p-5">
            <h3 className="text-base font-semibold text-white mb-4">Analysis</h3>
            <div className="space-y-3" data-testid="email-analysis">
              <InfoRow label="Email" value={results.email} />
              <InfoRow label="Format Valid" value={results.format_valid ? 'Yes' : 'No'} positive={results.format_valid} />
              <InfoRow label="Domain Exists" value={results.domain_exists ? 'Yes' : 'No'} positive={results.domain_exists} />
              <InfoRow label="Provider" value={results.provider_type} />
              <InfoRow label="Disposable" value={results.is_disposable ? 'YES — Suspicious' : 'No'} positive={!results.is_disposable} />
            </div>
            {results.mx_records.length > 0 && (
              <div className="mt-4">
                <span className="text-xs font-bold text-[#FF6B35] uppercase">MX Records</span>
                {results.mx_records.map((mx, i) => (
                  <p key={i} className="text-sm text-slate-300 font-mono ml-3">[{mx.priority}] {mx.server}</p>
                ))}
              </div>
            )}
          </div>
          {results.suggested_profiles.length > 0 && (
            <div className="card-premium rounded-2xl p-5">
              <h3 className="text-base font-semibold text-white mb-4">Suggested Profiles</h3>
              <div className="space-y-2">
                {results.suggested_profiles.map((p) => (
                  <a key={p.platform} href={p.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-3 rounded-xl bg-[#1a2556]/60 border border-white/10 hover:border-[#FF6B35]/40 transition-all">
                    <span className="text-white text-sm">{p.platform}</span>
                    <ExternalLink size={14} className="text-slate-400" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- Phone Intel Tab ---
const PhoneIntel = ({ loading, setLoading, results, setResults, setError }) => {
  const [phone, setPhone] = useState('');

  const lookup = async () => {
    if (!phone.trim()) return;
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(`${API}/phone-lookup`, { phone: phone.trim() }, { withCredentials: true });
      setResults(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Lookup failed');
    } finally { setLoading(false); }
  };

  return (
    <div>
      <div className="card-premium rounded-2xl p-6 mb-6">
        <h3 className="text-base font-semibold text-white mb-4">Phone Intelligence</h3>
        <p className="text-slate-400 text-xs mb-4">Country detection, format validation, messenger presence checks</p>
        <div className="flex gap-3">
          <input value={phone} onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && lookup()} placeholder="+48123456789" className="flex-1 bg-[#0B1437]/80 border border-white/10 rounded-xl h-11 px-4 text-white text-sm focus:outline-none focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35]" data-testid="phone-input" />
          <button onClick={lookup} disabled={loading || !phone.trim()} className="px-6 py-2.5 bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white font-bold rounded-xl disabled:opacity-40" data-testid="phone-search-btn">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
          </button>
        </div>
      </div>

      {results && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card-premium rounded-2xl p-5">
            <h3 className="text-base font-semibold text-white mb-4">Analysis</h3>
            <div className="space-y-3" data-testid="phone-analysis">
              <InfoRow label="Number" value={results.phone} />
              <InfoRow label="Valid Format" value={results.is_valid_format ? 'Yes' : 'No'} positive={results.is_valid_format} />
              {results.country && <InfoRow label="Country" value={`${results.country} (${results.country_code})`} />}
            </div>
          </div>
          <div className="card-premium rounded-2xl p-5">
            <h3 className="text-base font-semibold text-white mb-4">Messenger Checks</h3>
            <div className="space-y-2">
              {results.messenger_checks.map((m) => (
                <div key={m.platform} className="flex items-center justify-between p-3 rounded-xl bg-[#1a2556]/60 border border-white/10">
                  <div>
                    <span className="text-white text-sm font-medium">{m.platform}</span>
                    <p className="text-slate-500 text-xs">{m.note}</p>
                  </div>
                  {m.url && (
                    <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-[#FF6B35] hover:text-[#F7931E]">
                      <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Shared Components ---
const InfoRow = ({ label, value, positive }) => (
  <div className="flex justify-between items-center py-1.5 border-b border-white/5">
    <span className="text-xs uppercase text-slate-400 tracking-wider">{label}</span>
    <span className={`text-sm font-mono ${positive === true ? 'text-[#22c55e]' : positive === false ? 'text-[#ef4444]' : 'text-white'}`}>{value}</span>
  </div>
);

export default MHIntelligence;
